const DOM_MUTATION_GUARD_FLAG = '__iusproDomMutationGuardsInstalled__'

declare global {
	interface Window {
		[DOM_MUTATION_GUARD_FLAG]?: boolean
	}
}

/**
 * Some embedded hosts inject UI directly under `<html>` before the app starts.
 * React hydrates the whole document and treats those host-owned siblings as a
 * fatal structural mismatch, so keep them out of the tree until hydration has
 * claimed the server-rendered `<head>` and `<body>`.
 */
export function detachUnexpectedDocumentChildren(documentNode: Document) {
	const root = documentNode.documentElement
	const expectedChildren = new Set([documentNode.head, documentNode.body])
	const detachedChildren = new Map<Element, ChildNode | null>()

	const detach = (child: Element) => {
		if (expectedChildren.has(child as HTMLHeadElement | HTMLBodyElement) || detachedChildren.has(child)) return
		detachedChildren.set(child, child.nextSibling)
		root.removeChild(child)
	}

	for (const child of Array.from(root.children)) detach(child)

	// Embedded hosts can add their sibling after this module starts but before
	// React Router finishes receiving the streamed hydration data.
	const observer = new MutationObserver(records => {
		for (const record of records) {
			for (const child of Array.from(record.addedNodes)) {
				if (child instanceof Element && child.parentNode === root) detach(child)
			}
		}
	})
	observer.observe(root, { childList: true })

	return () => {
		observer.disconnect()
		for (const [child, nextSibling] of detachedChildren) {
			// The host may have reclaimed its node while React was hydrating.
			if (child.parentNode) continue
			root.insertBefore(child, nextSibling?.parentNode === root ? nextSibling : null)
		}
	}
}

/**
 * Browser translators and extensions sometimes mutate React-managed DOM nodes.
 * When React later removes a node it still "owns", the browser can throw if
 * that node was already detached or reparented externally.
 */
export function installDomMutationGuards() {
	if (typeof window === 'undefined' || window[DOM_MUTATION_GUARD_FLAG]) {
		return
	}

	window[DOM_MUTATION_GUARD_FLAG] = true

	const nativeRemoveChild = Node.prototype.removeChild
	const nativeInsertBefore = Node.prototype.insertBefore

	Node.prototype.removeChild = function patchedRemoveChild<T extends Node>(child: T) {
		if (child.parentNode !== this) {
			return child
		}

		return nativeRemoveChild.call(this, child) as T
	}

	Node.prototype.insertBefore = function patchedInsertBefore<T extends Node>(newChild: T, refChild: Node | null) {
		if (refChild && refChild.parentNode !== this) {
			return nativeInsertBefore.call(this, newChild, null) as T
		}

		return nativeInsertBefore.call(this, newChild, refChild) as T
	}
}
